// ActionControlsTab — list of controls linked to the current action, with link/unlink.
// Data comes from GET /actions/{action_id}/controls.
// Actions own this relationship via action_links table.

import { useState, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { actionsApi, type LinkedActionControl } from '@/lib/api/actions'
import {
  controlsApi,
  type Control,
  controlLifecycleStateLabels,
  controlLifecycleStateColors,
} from '@/lib/api/controls'
import { categoriesApi } from '@/lib/api/categories'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { LinkPickerDialog, type PickerTreeNode } from '@/components/shared/LinkPickerDialog'
import { ConfirmUnlinkDialog } from '@/components/shared/ConfirmUnlinkDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Shield,
  FolderOpen,
  Calendar,
  User,
  ExternalLink,
  Plus,
  Unlink,
} from 'lucide-react'
import { cn } from '@/lib/utils'

export function ActionControlsTab() {
  const { slug } = useTenant()
  const { actionId } = useParams({ strict: false }) as { actionId: string }
  const navigate = useNavigate()
  const api = useApi()
  const queryClient = useQueryClient()
  const actApi = actionsApi(api)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedActionControl | null>(null)

  // Linked controls for this action
  const {
    data: controls,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.actions.controls(slug, actionId),
    queryFn: () => actApi.controls(actionId),
  })

  // Control category tree for the picker — fetched when picker opens
  const { data: categoryTree, isLoading: categoriesLoading } = useQuery({
    queryKey: queryKeys.categories.tree(slug, 'control'),
    queryFn: () => categoriesApi(api, 'control').tree(),
    enabled: pickerOpen,
  })

  // All controls (permission-filtered) for the picker
  const { data: allControls, isLoading: controlsLoading } = useQuery({
    queryKey: queryKeys.controls.all(slug),
    queryFn: () => controlsApi(api).list(),
    enabled: pickerOpen,
  })

  const existingIds = useMemo(
    () => new Set(controls?.map((c) => c.control_id) ?? []),
    [controls],
  )

  // Unlink mutation
  const unlinkMutation = useMutation({
    mutationFn: ({ linkId, notes }: { linkId: string; notes: string }) =>
      actApi.unlinkControl(actionId, linkId, notes || undefined),
    onSuccess: () => {
      setUnlinkTarget(null)
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.controls(slug, actionId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
    },
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load linked controls"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header with count and add button */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {controls?.length ?? 0} linked control{controls?.length !== 1 ? 's' : ''}
        </div>
        <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Link Control
        </Button>
      </div>

      {/* Empty state */}
      {(!controls || controls.length === 0) && (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Shield className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm">No linked controls.</p>
            <p className="text-xs text-muted-foreground/60">
              Use the button above to link controls to this action.
            </p>
          </div>
        </div>
      )}

      {/* Control list */}
      {controls && controls.length > 0 && (
        <div className="divide-y rounded-lg border">
          {controls.map((control) => (
            <ControlRow
              key={control.link_id}
              control={control}
              onClick={() => navigate({ to: `/t/${slug}/controls/${control.control_id}` })}
              onUnlink={() => setUnlinkTarget(control)}
            />
          ))}
        </div>
      )}

      {/* Link picker dialog */}
      <LinkPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onLinked={() => {
          setPickerOpen(false)
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.controls(slug, actionId) })
          queryClient.invalidateQueries({ queryKey: queryKeys.actions.detail(slug, actionId) })
        }}
        title="Link Control"
        description="Select a control to link to this action."
        treeNodes={categoryTree as PickerTreeNode[] | undefined}
        objects={allControls}
        isLoading={controlsLoading || categoriesLoading}
        existingIds={existingIds}
        getObjectId={(c: Control) => c.id}
        getObjectParentId={(c: Control) => c.category_id}
        getObjectSearchText={(c: Control) => `${c.name} ${c.category_name}`}
        renderObject={(c: Control, isSelected: boolean) => (
          <ControlPickerRow control={c} isSelected={isSelected} />
        )}
        linkFn={(controlId: string) => actApi.linkControl(actionId, controlId)}
        linkButtonLabel="Link Control"
        searchPlaceholder="Search controls or categories..."
      />

      {/* Unlink confirmation dialog */}
      {unlinkTarget && (
        <ConfirmUnlinkDialog
          open={!!unlinkTarget}
          onClose={() => setUnlinkTarget(null)}
          onConfirm={(notes) =>
            unlinkMutation.mutate({ linkId: unlinkTarget.link_id, notes })
          }
          title="Remove Control Link"
          description={`Remove the link between this action and "${unlinkTarget.control_name}"?`}
          isPending={unlinkMutation.isPending}
        />
      )}
    </div>
  )
}

function ControlRow({
  control,
  onClick,
  onUnlink,
}: {
  control: LinkedActionControl
  onClick: () => void
  onUnlink: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Name */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{control.control_name}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
      </div>

      {/* Category */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FolderOpen className="h-3 w-3" />
        <span className="max-w-[120px] truncate">{control.category_name}</span>
      </div>

      {/* Owner */}
      {control.owner_display_name && (
        <div className="hidden items-center gap-1 text-xs text-muted-foreground lg:flex">
          <User className="h-3 w-3" />
          <span className="max-w-[100px] truncate">{control.owner_display_name}</span>
        </div>
      )}

      {/* Linked date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(control.linked_at).toLocaleDateString()}</span>
      </div>

      {/* Status badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          controlLifecycleStateColors[control.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {controlLifecycleStateLabels[control.lifecycle_state] ?? control.lifecycle_state}
      </Badge>

      {/* Unlink button */}
      <button
        className="flex-shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation()
          onUnlink()
        }}
        title="Remove link"
      >
        <Unlink className="h-4 w-4" />
      </button>
    </div>
  )
}

function ControlPickerRow({
  control,
  isSelected,
}: {
  control: Control
  isSelected: boolean
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className={cn('truncate', isSelected && 'font-medium')}>{control.name}</span>
      <Badge
        variant="outline"
        className={cn(
          'ml-auto flex-shrink-0 px-1.5 py-0 text-xs',
          controlLifecycleStateColors[control.lifecycle_state] ?? 'bg-gray-100 text-gray-700',
        )}
      >
        {controlLifecycleStateLabels[control.lifecycle_state] ?? control.lifecycle_state}
      </Badge>
    </div>
  )
}
